import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { requestJson } from "../../../../helpers/api";
import type {
  CajaDiaAnterior,
  SucursalRendicion,
  BancoReferencia,
  CuentaBancoReferencia,
} from "../../RendicionCaja/types";

type TipoDetalle = "efectivo" | "cheque" | "transferencia";

type DetalleDeposito = {
  id: number;
  tipo: TipoDetalle;
  descripcion: string;
  monto: number;
  banco?: string;
  numeroDocumento?: string;
  montoReferencia?: number | null;
  cajaReferenciaIdLocal?: number | null;
};

type ModalConflictoTipo = "cambioSucursal" | "seleccionCaja";
type ModalConflictoEstado = {
  visible: boolean;
  tipo: ModalConflictoTipo | null;
  sucursalObjetivo?: string;
  cajaPendiente?: string | null;
};

const crearDetalle = (): DetalleDeposito => ({
  id: Date.now(),
  tipo: "efectivo",
  descripcion: "",
  monto: 0,
  banco: "",
  numeroDocumento: "",
  montoReferencia: null,
  cajaReferenciaIdLocal: null,
});

const calcularResumen = (detalles: DetalleDeposito[]) =>
  detalles.reduce(
    (acc, item) => {
      acc.total += item.monto || 0;
      if (item.tipo === "efectivo") {
        acc.efectivo += item.monto || 0;
      } else if (item.tipo === "cheque") {
        acc.cheques += item.monto || 0;
      } else {
        acc.transferencias += item.monto || 0;
      }
      return acc;
    },
    { efectivo: 0, cheques: 0, transferencias: 0, total: 0 },
  );

const monedas = [
  { value: "PYG", label: "Guaraníes (PYG)" },
  { value: "USD", label: "Dólares (USD)" },
];

type DepositoBoletaFormProps = {
  cajasDisponibles: CajaDiaAnterior[];
  cargandoCajas?: boolean;
  errorCajas?: string | null;
  onReloadCajas?: () => void;
  sucursalesDisponibles: SucursalRendicion[];
  cargandoSucursales?: boolean;
  errorSucursales?: string | null;
  onReloadSucursales?: () => void;
  onSalirDeposito?: () => void;
  cajaFija?: CajaDiaAnterior | null;
};

const DepositoBoletaForm = ({
  cajasDisponibles,
  cargandoCajas = false,
  errorCajas = null,
  onReloadCajas,
  sucursalesDisponibles,
  cargandoSucursales = false,
  errorSucursales = null,
  onReloadSucursales,
  onSalirDeposito,
  cajaFija = null,
}: DepositoBoletaFormProps) => {
  const [banco, setBanco] = useState("");
  const [cuenta, setCuenta] = useState("");
  const [numeroBoleta, setNumeroBoleta] = useState("");
  const [fechaDeposito, setFechaDeposito] = useState("");
  const [horaDeposito, setHoraDeposito] = useState("");
  const [moneda, setMoneda] = useState("PYG");
  const [tipoCambio, setTipoCambio] = useState("1");
  const [sucursal, setSucursal] = useState("");
  const [caja, setCaja] = useState("");
  const [cajero, setCajero] = useState("");
  const [comprobanteTesoreria, setComprobanteTesoreria] = useState("");
  const [detalles, setDetalles] = useState<DetalleDeposito[]>([crearDetalle()]);
  const [observaciones, setObservaciones] = useState("");
  const [adjuntos, setAdjuntos] = useState<File[]>([]);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [sucursalIdSeleccionada, setSucursalIdSeleccionada] = useState(
    cajaFija?.idsucursal ? String(cajaFija.idsucursal) : "",
  );
  const [cajaIdsSeleccionadas, setCajaIdsSeleccionadas] = useState<string[]>(
    cajaFija ? [String(cajaFija.idcontrolcaja)] : [],
  );
  const [bancosReferencia, setBancosReferencia] = useState<BancoReferencia[]>([]);
  const [cargandoBancosReferencia, setCargandoBancosReferencia] = useState(false);
  const [errorBancosReferencia, setErrorBancosReferencia] = useState<string | null>(null);
  const [bancoReferenciaId, setBancoReferenciaId] = useState<number | null>(null);
  const [cuentasReferencia, setCuentasReferencia] = useState<Record<number, CuentaBancoReferencia[]>>({});
  const [cargandoCuentasReferencia, setCargandoCuentasReferencia] = useState<Record<number, boolean>>({});
  const [mostrarConfirmacionDiferencia, setMostrarConfirmacionDiferencia] = useState(false);
  const [mostrarModalExito, setMostrarModalExito] = useState(false);
  const [modalConflicto, setModalConflicto] = useState<ModalConflictoEstado>({
    visible: false,
    tipo: null,
    sucursalObjetivo: undefined,
    cajaPendiente: null,
  });
  const formatearFechaCorta = useCallback((valor?: string | null) => {
    if (!valor) {
      return "Sin fecha";
    }
    const fecha = new Date(valor);
    if (Number.isNaN(fecha.getTime())) {
      return valor;
    }
    return fecha.toLocaleString("es-PY", { dateStyle: "short", timeStyle: "short" });
  }, []);
  const formatearMontoGs = useCallback((valor?: number | null) => {
    const numero = typeof valor === "number" && Number.isFinite(valor) ? valor : 0;
    return numero.toLocaleString("es-PY", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }, []);
  const permiteSeleccionCajas = !cajaFija;
  const [mostrarSoloSinDeposito, setMostrarSoloSinDeposito] = useState(false);
  const [busquedaCajas, setBusquedaCajas] = useState("");

  const resumen = useMemo(() => calcularResumen(detalles), [detalles]);
  const sucursalOptions = useMemo(() => {
    if (sucursalesDisponibles.length > 0) {
      return sucursalesDisponibles.map((sucursalItem) => ({
        value: String(sucursalItem.idsucursal),
        label: sucursalItem.sucursalNombre ?? `Sucursal ${sucursalItem.idsucursal}`,
      }));
    }
    const mapa = new Map<string, string>();
    cajasDisponibles.forEach((cajaDisponible) => {
      if (cajaDisponible.idsucursal == null) {
        return;
      }
      const key = String(cajaDisponible.idsucursal);
      const label =
        cajaDisponible.sucursal ??
        cajaDisponible.sucursalNombre ??
        `Sucursal ${cajaDisponible.idsucursal}`;
      if (!mapa.has(key)) {
        mapa.set(key, label);
      }
    });
    return Array.from(mapa.entries()).map(([value, label]) => ({ value, label }));
  }, [cajasDisponibles, sucursalesDisponibles]);

  const cajaSeleccionadaInfo = useMemo(() => {
    const primerId = cajaIdsSeleccionadas[0];
    if (!primerId) {
      return undefined;
    }
    return cajasDisponibles.find(
      (cajaDisponible) => String(cajaDisponible.idcontrolcaja) === primerId,
    );
  }, [cajaIdsSeleccionadas, cajasDisponibles]);
  const cajasFiltradas = useMemo(() => {
    const listaBase = !sucursalIdSeleccionada
      ? cajasDisponibles
      : cajasDisponibles.filter(
          (cajaDisponible) =>
            cajaDisponible.idsucursal != null &&
            String(cajaDisponible.idsucursal) === sucursalIdSeleccionada,
        );
    const texto = busquedaCajas.trim().toLowerCase();
    const listaFiltrada = listaBase.filter((caja) => {
      if (mostrarSoloSinDeposito && (caja.depositosRegistrados?.length ?? 0) > 0) {
        return false;
      }
      if (!texto) {
        return true;
      }
      const candidatos = [
        caja.sucursal,
        caja.sucursalNombre,
        caja.cajero,
        caja.cajeroRegistrado,
        caja.turno,
        caja.descEstadoCaja,
        caja.descEstadoTurno,
        caja.idcontrolcaja != null ? String(caja.idcontrolcaja) : "",
      ];
      return candidatos.some((campo) => campo?.toLowerCase().includes(texto));
    });
    const obtenerTimestamp = (caja: CajaDiaAnterior) => {
      const referencia = caja.rendicionRegistradaEn ?? caja.fechaCierre ?? caja.fechaApertura ?? null;
      if (!referencia) {
        return 0;
      }
      const fecha = new Date(referencia);
      const tiempo = fecha.getTime();
      return Number.isNaN(tiempo) ? 0 : tiempo;
    };
    return [...listaFiltrada].sort((a, b) => obtenerTimestamp(b) - obtenerTimestamp(a));
  }, [busquedaCajas, cajasDisponibles, mostrarSoloSinDeposito, sucursalIdSeleccionada]);
  const cajasSeleccionadas = useMemo(() => {
    if (cajaIdsSeleccionadas.length === 0) {
      return [];
    }
    return cajaIdsSeleccionadas
      .map((id) => cajasDisponibles.find((cajaDisponible) => String(cajaDisponible.idcontrolcaja) === id))
      .filter((caja): caja is CajaDiaAnterior => Boolean(caja));
  }, [cajaIdsSeleccionadas, cajasDisponibles]);
  const cuentasBancoSeleccionado =
    bancoReferenciaId != null ? cuentasReferencia[bancoReferenciaId] ?? [] : [];
  const cargandoCuentasSeleccionado =
    bancoReferenciaId != null ? cargandoCuentasReferencia[bancoReferenciaId] ?? false : false;
  const totalEfectivoDeclaradoSeleccionado = useMemo(() => {
    if (cajasSeleccionadas.length === 0) {
      return 0;
    }
    return cajasSeleccionadas.reduce((acc, caja) => {
      const monto = caja.efectivoDeclarado ?? caja.totalFondoDeclarado ?? 0;
      if (typeof monto !== "number" || Number.isNaN(monto)) {
        return acc;
      }
      return acc + monto;
    }, 0);
  }, [cajasSeleccionadas]);
  const diferenciaVsDeclarado = resumen.total - totalEfectivoDeclaradoSeleccionado;
  const requiereConfirmacionDiferencia =
    cajasSeleccionadas.length > 0 && Math.abs(diferenciaVsDeclarado) >= 1;

  const actualizarDetalle = useCallback((id: number, cambios: Partial<DetalleDeposito>) => {
    setDetalles((prev) => prev.map((item) => (item.id === id ? { ...item, ...cambios } : item)));
  }, []);

  const manejarAdjuntos = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    setAdjuntos(files ? Array.from(files) : []);
  }, []);
  const obtenerCuentasBancoReferencia = useCallback(
    async (bancoId: number) => {
      if (!sucursalIdSeleccionada) {
        return [];
      }
      setCargandoCuentasReferencia((prev) => ({ ...prev, [bancoId]: true }));
      try {
        const timestamp = Date.now().toString();
        const data = await requestJson<CuentaBancoReferencia[]>(
          `/api/rendiciones/bancos/${bancoId}/cuentas?sucursalId=${sucursalIdSeleccionada}&_=${timestamp}`,
        );
        const lista = Array.isArray(data) ? data : [];
        setCuentasReferencia((prev) => ({ ...prev, [bancoId]: lista }));
        return lista;
      } catch (err) {
        setCuentasReferencia((prev) => ({ ...prev, [bancoId]: [] }));
        setErrorBancosReferencia(
          err instanceof Error ? err.message : "No pudimos obtener las cuentas del banco.",
        );
        return [];
      } finally {
        setCargandoCuentasReferencia((prev) => ({ ...prev, [bancoId]: false }));
      }
    },
    [sucursalIdSeleccionada],
  );

  const manejarBancoSelect = useCallback(
    (valor: string) => {
      if (!valor) {
        setBancoReferenciaId(null);
        setBanco("");
        setCuenta("");
        return;
      }
      const bancoId = Number(valor);
      if (!Number.isFinite(bancoId)) {
        setBanco(valor);
        setBancoReferenciaId(null);
        setCuenta("");
        return;
      }
      setBancoReferenciaId(bancoId);
      const encontrado = bancosReferencia.find((ref) => ref.id === bancoId);
      setBanco(encontrado?.descripcion ?? `Banco ${bancoId}`);
      setCuenta("");
      if (encontrado && Array.isArray(encontrado.cuentas) && encontrado.cuentas.length > 0) {
        setCuentasReferencia((prev) => ({ ...prev, [bancoId]: encontrado.cuentas ?? [] }));
      } else {
        void obtenerCuentasBancoReferencia(bancoId);
      }
    },
    [bancosReferencia, obtenerCuentasBancoReferencia],
  );

  useEffect(() => {
    let activo = true;
    if (!sucursalIdSeleccionada) {
      setBancosReferencia([]);
      setBancoReferenciaId(null);
      setBanco("");
      setCuenta("");
      return;
    }
    const timestamp = Date.now().toString();
    setCargandoBancosReferencia(true);
    setErrorBancosReferencia(null);
    requestJson<BancoReferencia[]>(
      `/api/rendiciones/bancos?sucursalId=${sucursalIdSeleccionada}&_=${timestamp}`,
    )
      .then((data) => {
        if (!activo) return;
        const lista = Array.isArray(data) ? data : [];
        setBancosReferencia(lista);
        const cuentasMap = lista.reduce<Record<number, CuentaBancoReferencia[]>>((acc, bancoRef) => {
          if (bancoRef.id && Array.isArray(bancoRef.cuentas)) {
            acc[bancoRef.id] = bancoRef.cuentas;
          }
          return acc;
        }, {});
        setCuentasReferencia((prev) => ({ ...prev, ...cuentasMap }));
        if (bancoReferenciaId) {
          const existe = lista.some((bancoRef) => bancoRef.id === bancoReferenciaId);
          if (!existe) {
            setBancoReferenciaId(null);
            setBanco("");
            setCuenta("");
          }
        }
      })
      .catch((err) => {
        if (!activo) return;
        setErrorBancosReferencia(
          err instanceof Error ? err.message : "No pudimos obtener los bancos de referencia.",
        );
        setBancosReferencia([]);
        setBancoReferenciaId(null);
      })
      .finally(() => {
        if (activo) {
          setCargandoBancosReferencia(false);
        }
      });
    return () => {
      activo = false;
    };
  }, [sucursalIdSeleccionada, bancoReferenciaId, obtenerCuentasBancoReferencia]);

  const payload = useMemo(
    () => ({
      banco,
      cuenta,
      numeroBoleta,
      fechaDeposito,
      horaDeposito,
      moneda,
      tipoCambio: Number(tipoCambio) || 1,
      sucursal,
      caja,
      cajero,
      bancoReferenciaId,
      sucursalReferenciaId: sucursalIdSeleccionada ? Number(sucursalIdSeleccionada) : null,
      cajaReferenciaId: cajaIdsSeleccionadas[0] ? Number(cajaIdsSeleccionadas[0]) : null,
      cajasReferenciaIds: cajaIdsSeleccionadas
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id)),
      comprobanteTesoreria,
      observaciones,
      detalles: detalles.map(({ id, cajaReferenciaIdLocal, montoReferencia, ...resto }) => resto),
      resumen,
      adjuntos: adjuntos.map((file) => ({ nombre: file.name, size: file.size, type: file.type })),
    }),
    [
      adjuntos,
      banco,
      caja,
      cajaIdsSeleccionadas,
      bancoReferenciaId,
      cajero,
      comprobanteTesoreria,
      cuenta,
      detalles,
      fechaDeposito,
      horaDeposito,
      moneda,
      numeroBoleta,
      observaciones,
      resumen,
      sucursal,
      sucursalIdSeleccionada,
      tipoCambio,
    ],
  );

  const ejecutarRegistro = useCallback(async () => {
    setEnviando(true);
    setMensaje(null);
    setError(null);
    try {
      await requestJson("/api/tesoreria/depositos", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setMensaje("Depósito registrado correctamente.");
      setDetalles([crearDetalle()]);
      setAdjuntos([]);
      setObservaciones("");
      setNumeroBoleta("");
      setCuenta("");
      setBanco("");
      setBancoReferenciaId(null);
      setSucursal("");
      setCaja("");
      setCajaIdsSeleccionadas([]);
      setSucursalIdSeleccionada("");
      setCajero("");
      setMostrarModalExito(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No pudimos registrar el depósito.");
    } finally {
      setEnviando(false);
    }
  }, [payload]);

  const manejarRegistro = useCallback(() => {
    if (requiereConfirmacionDiferencia) {
      setMostrarConfirmacionDiferencia(true);
      return;
    }
    void ejecutarRegistro();
  }, [ejecutarRegistro, requiereConfirmacionDiferencia]);

  const confirmarRegistroConDiferencia = useCallback(() => {
    setMostrarConfirmacionDiferencia(false);
    void ejecutarRegistro();
  }, [ejecutarRegistro]);

  const cancelarConfirmacionDiferencia = useCallback(() => {
    setMostrarConfirmacionDiferencia(false);
  }, []);

  const manejarSeguirCargando = useCallback(() => {
    setMostrarModalExito(false);
  }, []);

  const manejarSalirDeposito = useCallback(() => {
    setMostrarModalExito(false);
    onSalirDeposito?.();
  }, [onSalirDeposito]);

  const cerrarModalConflicto = useCallback(() => {
    setModalConflicto({ visible: false, tipo: null, sucursalObjetivo: undefined, cajaPendiente: null });
  }, []);

  const confirmarModalConflicto = useCallback(() => {
    if (modalConflicto.tipo === "cambioSucursal") {
      setCajaIdsSeleccionadas([]);
      setSucursalIdSeleccionada(modalConflicto.sucursalObjetivo ?? "");
    } else if (modalConflicto.tipo === "seleccionCaja") {
      setCajaIdsSeleccionadas(modalConflicto.cajaPendiente ? [modalConflicto.cajaPendiente] : []);
    }
    setModalConflicto({ visible: false, tipo: null, sucursalObjetivo: undefined, cajaPendiente: null });
  }, [modalConflicto]);

  const manejarCambioSucursalSelect = useCallback(
    (valor: string) => {
      if (!permiteSeleccionCajas) {
        return;
      }
      if (cajaIdsSeleccionadas.length > 0 && valor !== sucursalIdSeleccionada) {
        setModalConflicto({
          visible: true,
          tipo: "cambioSucursal",
          sucursalObjetivo: valor,
          cajaPendiente: null,
        });
        return;
      }
      setSucursalIdSeleccionada(valor);
      setCajaIdsSeleccionadas([]);
    },
    [cajaIdsSeleccionadas, sucursalIdSeleccionada, permiteSeleccionCajas],
  );

  const toggleCajaSeleccion = useCallback(
    (id: string) => {
      if (!permiteSeleccionCajas) {
        return;
      }
      const yaSeleccionada = cajaIdsSeleccionadas.includes(id);
      if (yaSeleccionada) {
        setCajaIdsSeleccionadas((prev) => prev.filter((item) => item !== id));
        return;
      }
      const cajaNueva = cajasDisponibles.find((cajaDisponible) => String(cajaDisponible.idcontrolcaja) === id);
      if (!cajaNueva) {
        return;
      }
      if (cajaIdsSeleccionadas.length > 0) {
        const primeraCaja = cajasDisponibles.find(
          (cajaDisponible) => String(cajaDisponible.idcontrolcaja) === cajaIdsSeleccionadas[0],
        );
        const sucNueva = cajaNueva.idsucursal != null ? String(cajaNueva.idsucursal) : null;
        const sucPrimera = primeraCaja?.idsucursal != null ? String(primeraCaja.idsucursal) : null;
        if (sucNueva && sucPrimera && sucNueva !== sucPrimera) {
          setModalConflicto({
            visible: true,
            tipo: "seleccionCaja",
            cajaPendiente: id,
            sucursalObjetivo: undefined,
          });
          return;
        }
      }
      setCajaIdsSeleccionadas((prev) => [...prev, id]);
    },
    [cajaIdsSeleccionadas, cajasDisponibles, permiteSeleccionCajas],
  );

  const esFormularioValido =
    banco.trim() &&
    cuenta.trim() &&
    numeroBoleta.trim() &&
    fechaDeposito &&
    (cajaIdsSeleccionadas.length > 0 || (sucursal.trim() && caja.trim())) &&
    resumen.total > 0;

  useEffect(() => {
    if (cajaFija) {
      setSucursalIdSeleccionada(cajaFija.idsucursal ? String(cajaFija.idsucursal) : "");
      setCajaIdsSeleccionadas([String(cajaFija.idcontrolcaja)]);
    }
  }, [cajaFija]);

  useEffect(() => {
    if (!sucursalIdSeleccionada) {
      setSucursal("");
      return;
    }
    const seleccionado = sucursalOptions.find((opt) => opt.value === sucursalIdSeleccionada);
    setSucursal(seleccionado?.label ?? "");
  }, [sucursalIdSeleccionada, sucursalOptions]);

  useEffect(() => {
    setCajaIdsSeleccionadas((prev) =>
      prev.filter((id) => cajasDisponibles.some((cajaDisponible) => String(cajaDisponible.idcontrolcaja) === id)),
    );
  }, [cajasDisponibles]);

  useEffect(() => {
    if (cajaIdsSeleccionadas.length === 0) {
      setDetalles((prev) => {
        const manuales = prev.filter((detalle) => detalle.cajaReferenciaIdLocal == null);
        return manuales.length > 0 ? manuales : [crearDetalle()];
      });
      return;
    }
    setDetalles((prev) => {
      const mapaPrev = new Map<string, DetalleDeposito>();
      prev
        .filter((detalle) => detalle.cajaReferenciaIdLocal != null)
        .forEach((detalle) => {
          mapaPrev.set(String(detalle.cajaReferenciaIdLocal), detalle);
        });
      const nuevos: DetalleDeposito[] = [];
      cajaIdsSeleccionadas.forEach((id, index) => {
        const caja = cajasDisponibles.find(
          (cajaDisponible) => String(cajaDisponible.idcontrolcaja) === id,
        );
        if (!caja) {
          return;
        }
        const clave = String(caja.idcontrolcaja);
        const previo = mapaPrev.get(clave);
        const montoReferencia =
          typeof caja.efectivoDeclarado === "number"
            ? caja.efectivoDeclarado
            : typeof caja.totalFondoDeclarado === "number"
            ? caja.totalFondoDeclarado
            : 0;
        const numeroCaja = Number(id);
        nuevos.push({
          id: previo?.id ?? (Number.isFinite(numeroCaja) ? numeroCaja : Date.now() + index),
          tipo: "efectivo",
          descripcion:
            caja.descEstadoCaja ??
            caja.turno ??
            `Caja #${caja.idcontrolcaja}`,
          monto: previo?.monto ?? (Number.isFinite(montoReferencia) ? montoReferencia : 0),
          banco: previo?.banco ?? "",
          numeroDocumento: previo?.numeroDocumento ?? "",
          montoReferencia: Number.isFinite(montoReferencia) ? montoReferencia : 0,
          cajaReferenciaIdLocal: caja.idcontrolcaja,
        });
      });
      return nuevos.length > 0 ? nuevos : prev;
    });
  }, [cajaIdsSeleccionadas, cajasDisponibles]);

  useEffect(() => {
    if (bancoReferenciaId == null) {
      return;
    }
    if (!cargandoCuentasSeleccionado && cuentasBancoSeleccionado.length === 1) {
      const unica = cuentasBancoSeleccionado[0];
      if (unica?.numero) {
        setCuenta(unica.numero);
      }
    }
  }, [bancoReferenciaId, cuentasBancoSeleccionado, cargandoCuentasSeleccionado]);

  useEffect(() => {
    if (cajaIdsSeleccionadas.length === 0) {
      setCaja("");
      return;
    }
    if (cajaSeleccionadaInfo) {
      const etiqueta =
        cajaSeleccionadaInfo.descEstadoCaja ??
        cajaSeleccionadaInfo.turno ??
        `Caja #${cajaSeleccionadaInfo.idcontrolcaja}`;
      setCaja(
        cajaIdsSeleccionadas.length > 1
          ? `Varias cajas seleccionadas (${cajaIdsSeleccionadas.length})`
          : `${etiqueta} (#${cajaSeleccionadaInfo.idcontrolcaja})`,
      );
      if (cajaIdsSeleccionadas.length === 1) {
        if (cajaSeleccionadaInfo.cajero) {
          setCajero(cajaSeleccionadaInfo.cajero);
        }
      } else if (cajaIdsSeleccionadas.length > 1) {
        setCajero("");
      }
      if (
        cajaSeleccionadaInfo.idsucursal != null &&
        String(cajaSeleccionadaInfo.idsucursal) !== sucursalIdSeleccionada
      ) {
        setSucursalIdSeleccionada(String(cajaSeleccionadaInfo.idsucursal));
      }
    }
  }, [cajaIdsSeleccionadas, cajaSeleccionadaInfo, sucursalIdSeleccionada]);

  return (
    <Card>
      <CardBody>
        <Form onSubmit={(event) => event.preventDefault()}>
          {mensaje ? <Alert color="success">{mensaje}</Alert> : null}
          {error ? <Alert color="danger">{error}</Alert> : null}
          {permiteSeleccionCajas ? (
            <>
          <div className="mb-4">
            <h4 className="mb-1">Origen del fondo</h4>
            <p className="text-muted mb-3">
              Indicá la sucursal, caja y cajero asociados a este depósito. Podés marcar una o varias cajas del listado para vincularlas al mismo comprobante.
            </p>
            <Row className="gy-3">
              <Col md={4}>
                <Label className="form-label">Sucursal</Label>
                <Input
                  type="select"
                  value={sucursalIdSeleccionada}
                  onChange={(e) => manejarCambioSucursalSelect(e.target.value)}
                >
                  <option value="">Todas las sucursales disponibles</option>
                  {sucursalOptions.map((opcion) => (
                    <option key={opcion.value} value={opcion.value}>
                      {opcion.label}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>
            <div className="mt-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="text-muted small">
                {cargandoSucursales
                  ? "Cargando sucursales asignadas…"
                  : errorSucursales
                  ? "No pudimos cargar tus sucursales."
                  : `Sucursales disponibles: ${sucursalOptions.length}`}
              </div>
              <Button
                color="link"
                className="p-0"
                onClick={() => onReloadSucursales?.()}
                disabled={cargandoSucursales || !onReloadSucursales}
              >
                <i className="ri-refresh-line me-1" />
                Actualizar sucursales
              </Button>
            </div>
            {errorSucursales ? (
              <Alert color="warning" className="mt-2">
                {errorSucursales}
              </Alert>
            ) : null}
            <div className="mt-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <div className="text-muted small">Listado de cajas más recientes.</div>
                <Button
                  color="link"
                  className="p-0"
                  onClick={() => onReloadCajas?.()}
                  disabled={cargandoCajas || !onReloadCajas}
                >
                  <i className="ri-refresh-line me-1" />
                  Actualizar listado
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                <div className="form-check form-switch m-0">
                  <Input
                    type="switch"
                    role="switch"
                    className="form-check-input"
                    id="switch-sin-deposito"
                    checked={mostrarSoloSinDeposito}
                    onChange={(e) => setMostrarSoloSinDeposito(e.target.checked)}
                  />
                  <Label className="form-check-label text-muted small ms-2" htmlFor="switch-sin-deposito">
                    Solo mostrar cajas sin depósito
                  </Label>
                </div>
                <div className="ms-auto" style={{ minWidth: "240px" }}>
                  <Input
                    type="search"
                    placeholder="Buscar sucursal, cajero o caja…"
                    value={busquedaCajas}
                    onChange={(e) => setBusquedaCajas(e.target.value)}
                  />
                </div>
              </div>
              {cargandoCajas ? (
                <div className="text-center text-muted py-3">Cargando cajas…</div>
              ) : errorCajas ? (
                <Alert color="warning" className="mb-0">
                  {errorCajas}
                </Alert>
              ) : cajasFiltradas.length === 0 ? (
                <div className="text-center text-muted py-3">No encontramos cajas activas en el periodo seleccionado.</div>
              ) : (
                <div className="table-responsive" style={{ maxHeight: 260 }}>
                  <Table hover size="sm" className="table-nowrap align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "40px" }} />
                        <th className="text-center">Nro. caja</th>
                        <th>Detalle</th>
                        <th style={{ minWidth: 220 }}>Registro cajero</th>
                        <th style={{ minWidth: 220 }}>Depósitos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cajasFiltradas.map((cajaDisponible) => {
                        const idCaja = String(cajaDisponible.idcontrolcaja);
                        const seleccionado = cajaIdsSeleccionadas.includes(idCaja);
                        const fechaDetalleReferencia =
                          cajaDisponible.fechaApertura ??
                          cajaDisponible.fechaHoraAperturaTurno ??
                          cajaDisponible.fechaCierre ??
                          null;
                        const fechaRegistroReferencia = cajaDisponible.rendicionRegistradaEn ?? null;
                        const formatearFechaCustom = (valor?: string | null, incluirHora = true) => {
                          if (!valor) {
                            return "Sin fecha";
                          }
                          const date = new Date(valor);
                          if (Number.isNaN(date.getTime())) {
                            return valor;
                          }
                          return incluirHora
                            ? date.toLocaleString("es-PY", { dateStyle: "short", timeStyle: "short" })
                            : date.toLocaleDateString("es-PY", { dateStyle: "short" });
                        };
                        const fechaDetalle = formatearFechaCustom(fechaDetalleReferencia);
                        const fechaRegistro = formatearFechaCustom(fechaRegistroReferencia);
                        const usuarioRegistro = cajaDisponible.rendicionUsuario ?? null;
                        return (
                        <tr
                          key={cajaDisponible.idcontrolcaja}
                          role="button"
                          className={seleccionado ? "table-active" : undefined}
                          onClick={() => toggleCajaSeleccion(idCaja)}
                        >
                          <td>
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              checked={seleccionado}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleCajaSeleccion(idCaja);
                              }}
                            />
                          </td>
                            <td className="text-center fw-semibold">{cajaDisponible.idcontrolcaja}</td>
                            <td>
                              <div className="fw-semibold">
                                {cajaDisponible.sucursal ?? cajaDisponible.sucursalNombre ?? "Sin sucursal"}
                              </div>
                              <div className="text-muted small">
                                Cajero: {cajaDisponible.cajeroRegistrado ?? "Sin datos"}
                              </div>
                              <div className="text-muted small">
                                Turno: {cajaDisponible.turno ?? "Sin turno"}
                              </div>
                              <div className="text-muted small">
                                Fecha: {fechaDetalle}
                              </div>
                            </td>
                            <td>
                              <div className="fw-semibold">
                                {typeof cajaDisponible.totalFondoDeclarado === "number"
                                  ? `Gs. ${cajaDisponible.totalFondoDeclarado.toLocaleString("es-PY", {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    })}`
                                  : "Sin monto"}
                              </div>
                              <small className="text-muted d-block">
                                Cajero: {cajaDisponible.cajeroRegistrado ?? "Sin datos"}
                              </small>
                              <small className="text-muted d-block">
                                Declarado por: {usuarioRegistro ?? "Sin registro"}
                              </small>
                              <small className="text-muted d-block">
                                Registrado el: {fechaRegistro}
                              </small>
                            </td>
                            <td>
                              {cajaDisponible.depositosRegistrados && cajaDisponible.depositosRegistrados.length > 0 ? (
                                cajaDisponible.depositosRegistrados.map((deposito) => {
                                  const responsable =
                                    deposito.registradoPorNombre ??
                                    deposito.registradoPorCorreo ??
                                    (deposito.registradoPorId
                                      ? `ID ${deposito.registradoPorId}`
                                      : "Sin usuario");
                                  return (
                                    <div key={`${deposito.id}-${deposito.numeroBoleta ?? ""}`} className="mb-2">
                                      <div className="fw-semibold">
                                        {deposito.numeroBoleta ?? `Depósito #${deposito.id}`}
                                      </div>
                                      <div className="text-muted small">
                                        {deposito.banco ?? "Banco sin nombre"} · {formatearMontoGs(deposito.montoTotal)}{" "}
                                        {deposito.moneda ?? "PYG"}
                                      </div>
                                      <div className="text-muted small">
                                        Registrado por: {responsable}
                                      </div>
                                      <div className="text-muted small">
                                        Fecha depósito: {formatearFechaCustom(deposito.fechaDeposito ?? null, false)}
                                      </div>
                                      <div className="text-muted small">
                                        Registrado en sistema: {formatearFechaCustom(deposito.creadoEn ?? null)}
                                      </div>
                                      {deposito.actualizadoEn ? (
                                        <div className="text-muted small">
                                          Actualizado en: {formatearFechaCustom(deposito.actualizadoEn, false)}
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })
                              ) : (
                                <span className="badge bg-light text-dark">Sin depósito</span>
                              )}
                            </td>
                         </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          </div>
            </>
          ) : (
            <div className="mb-4">
              <h4 className="mb-1">Origen del fondo</h4>
              <p className="text-muted mb-3">Registrarás un depósito para esta caja.</p>
              <div className="border rounded p-3 bg-light-subtle">
                <div className="fw-semibold">
                  {cajaFija?.sucursal ?? cajaFija?.sucursalNombre ?? "Sucursal sin nombre"}
                </div>
                <div className="text-muted small">
                  Caja #{cajaFija?.idcontrolcaja} · {cajaFija?.turno ?? "Sin turno"}
                </div>
                <div className="text-muted small">
                  Cajero: {cajaFija?.cajero ?? cajaFija?.cajeroRegistrado ?? "Sin datos"}
                </div>
              </div>
            </div>
          )}

          <div className="mb-4">
            <h4 className="mb-1">Datos del depósito</h4>
            <p className="text-muted mb-3">Información base para registrar la boleta conforme a requisitos contables.</p>
            <Row className="gy-3">
              <Col md={4}>
                <Label className="form-label">Banco</Label>
                {sucursalIdSeleccionada && bancosReferencia.length > 0 ? (
                  <>
                    <Input
                      type="select"
                      value={bancoReferenciaId ? String(bancoReferenciaId) : ""}
                      onChange={(e) => manejarBancoSelect(e.target.value)}
                      className="mb-2"
                    >
                      <option value="">Seleccioná un banco</option>
                      {bancosReferencia.map((bancoRef) => (
                        <option key={bancoRef.id} value={String(bancoRef.id)}>
                          {bancoRef.descripcion ?? `Banco ${bancoRef.id}`}
                        </option>
                      ))}
                    </Input>
                    {cargandoBancosReferencia ? (
                      <div className="text-muted small">Cargando bancos…</div>
                    ) : errorBancosReferencia ? (
                      <div className="text-danger small">{errorBancosReferencia}</div>
                    ) : null}
                  </>
                ) : (
                  <Input
                    value={banco}
                    onChange={(e) => {
                      setBanco(e.target.value);
                      setBancoReferenciaId(null);
                    }}
                    required
                    placeholder="Banco depositario"
                  />
                )}
              </Col>
              <Col md={4}>
                <Label className="form-label">Cuenta bancaria</Label>
                {bancoReferenciaId && (cuentasBancoSeleccionado.length > 0 || cargandoCuentasSeleccionado) ? (
                  cargandoCuentasSeleccionado ? (
                    <div className="text-muted small">Cargando cuentas…</div>
                  ) : (
                    <Input
                      type="select"
                      value={cuenta}
                      onChange={(e) => setCuenta(e.target.value)}
                      required
                    >
                      <option value="">Seleccioná una cuenta</option>
                      {cuentasBancoSeleccionado.map((cuentaRef) => (
                        <option
                          key={cuentaRef.id ?? `${cuentaRef.numero}-${cuentaRef.bancoId ?? ""}`}
                          value={cuentaRef.numero ?? ""}
                        >
                          {cuentaRef.numero ?? "Cuenta sin número"}
                        </option>
                      ))}
                    </Input>
                  )
                ) : (
                  <Input
                    value={cuenta}
                    onChange={(e) => setCuenta(e.target.value)}
                    required
                    placeholder="001-123456-7"
                  />
                )}
              </Col>
              <Col md={4}>
                <Label className="form-label">N° de boleta</Label>
                <Input
                  value={numeroBoleta}
                  onChange={(e) => setNumeroBoleta(e.target.value)}
                  required
                  placeholder="0001234"
                />
              </Col>
              <Col md={4}>
                <Label className="form-label">Fecha</Label>
                <Input type="date" value={fechaDeposito} onChange={(e) => setFechaDeposito(e.target.value)} required />
              </Col>
              <Col md={4}>
                <Label className="form-label">Hora</Label>
                <Input type="time" value={horaDeposito} onChange={(e) => setHoraDeposito(e.target.value)} />
              </Col>
              <Col md={4}>
                <Label className="form-label">Moneda</Label>
                <Input type="select" value={moneda} onChange={(e) => setMoneda(e.target.value)}>
                  {monedas.map((opcion) => (
                    <option key={opcion.value} value={opcion.value}>
                      {opcion.label}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={4}>
                <Label className="form-label">Tipo de cambio</Label>
                <Input
                  value={tipoCambio}
                  onChange={(e) => setTipoCambio(e.target.value)}
                  disabled={moneda === "PYG"}
                />
              </Col>
            </Row>
          </div>

          <div className="mb-4">
            <h4 className="mb-1">Detalle del depósito</h4>
            <p className="text-muted mb-3">
              Incluí cada forma documental (efectivo por denominación, cheques o transferencias) que respalda la boleta.
            </p>
            {cajaIdsSeleccionadas.length === 0 ? (
              <div className="alert alert-info mb-0">
                Seleccioná una o varias cajas para generar automáticamente el detalle del depósito.
              </div>
            ) : (
              <>
                <Table responsive bordered className="align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Caja / Punto</th>
                      <th className="text-end">Efectivo declarado</th>
                      <th className="text-end">Monto del depósito</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalles.map((detalle) => {
                      const caja = detalle.cajaReferenciaIdLocal
                        ? cajasDisponibles.find(
                            (cajaDisponible) => cajaDisponible.idcontrolcaja === detalle.cajaReferenciaIdLocal,
                          )
                        : null;
                      const sucursalCaja = caja?.sucursal ?? caja?.sucursalNombre ?? null;
                      const etiquetaCaja =
                        caja?.descEstadoCaja ??
                        caja?.turno ??
                        (caja ? `Caja #${caja.idcontrolcaja}` : "Caja seleccionada");
                      return (
                        <tr key={detalle.id}>
                          <td>
                            <div className="fw-semibold">
                              {detalle.descripcion || etiquetaCaja}
                            </div>
                            {caja ? (
                              <div className="text-muted small">
                                ID #{caja.idcontrolcaja}
                              </div>
                            ) : null}
                            {sucursalCaja ? <div className="text-muted small">{sucursalCaja}</div> : null}
                          </td>
                          <td className="text-end">
                            <strong>
                              {(detalle.montoReferencia ?? 0).toLocaleString("es-PY", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}
                            </strong>{" "}
                            Gs.
                          </td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              className="text-end"
                              value={detalle.monto}
                              onChange={(e) =>
                                actualizarDetalle(detalle.id, { monto: Number(e.target.value) || 0 })
                              }
                              required
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <p className="text-muted small mb-0 mt-2">
                  Estos valores se completan con el efectivo declarado por caja. Ajustalos si necesitás distribuir diferencias.
                </p>
              </>
            )}
            <Row className="mt-3 text-end">
              <Col md={{ size: 4, offset: 8 }}>
                <div className="border rounded p-3 bg-light-subtle">
                  <div className="d-flex justify-content-between">
                    <span>Efectivo</span>
                    <strong>
                      {resumen.efectivo.toLocaleString("es-PY", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Gs.
                    </strong>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between">
                    <span>Total</span>
                    <strong>
                      {resumen.total.toLocaleString("es-PY", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Gs.
                    </strong>
                  </div>
                  {cajasSeleccionadas.length > 0 ? (
                    <>
                      <hr />
                      <div className="d-flex justify-content-between">
                        <span>Total efectivo cajas seleccionadas</span>
                        <strong>
                          {totalEfectivoDeclaradoSeleccionado.toLocaleString("es-PY", {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}{" "}
                          Gs.
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Diferencia vs depósito</span>
                        <strong
                          className={
                            Math.abs(diferenciaVsDeclarado) < 1 ? "text-success" : diferenciaVsDeclarado > 0 ? "text-warning" : "text-danger"
                          }
                        >
                          {diferenciaVsDeclarado > 0 ? "+" : ""}
                          {diferenciaVsDeclarado.toLocaleString("es-PY", {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}{" "}
                          Gs.
                        </strong>
                      </div>
                    </>
                  ) : null}
                </div>
              </Col>
            </Row>
          </div>

          <div className="mb-4">
            <h4 className="mb-1">Documentos respaldatorios</h4>
            <p className="text-muted mb-3">Adjuntá foto o escaneo de la boleta y documentación bancaria.</p>
            <Input type="file" multiple onChange={manejarAdjuntos} />
            {adjuntos.length > 0 ? (
              <ul className="mt-2 small text-muted">
                {adjuntos.map((archivo) => (
                  <li key={archivo.name}>
                    {archivo.name} ({Math.round(archivo.size / 1024)} KB)
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="mb-4">
            <Label className="form-label">Observaciones</Label>
            <Input
              type="textarea"
              rows={4}
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Notas para contabilidad o tesorería"
            />
          </div>

          <div className="text-end">
            <Button color="success" disabled={!esFormularioValido || enviando} onClick={manejarRegistro}>
              {enviando ? (
                <>
                  <i className="ri-loader-2-line me-1 spinner-border spinner-border-sm" /> Guardando…
                </>
              ) : (
                <>
                  <i className="ri-bank-card-line me-1" />
                  Registrar depósito
                </>
              )}
            </Button>
          </div>
        </Form>
      </CardBody>

      <Modal isOpen={mostrarConfirmacionDiferencia} toggle={cancelarConfirmacionDiferencia} centered>
        <ModalHeader toggle={cancelarConfirmacionDiferencia}>Diferencia en efectivo</ModalHeader>
        <ModalBody>
          <p className="mb-2">
            El total a depositar es{" "}
            <strong>
              {resumen.total.toLocaleString("es-PY", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Gs.
            </strong>
            , pero el efectivo declarado en las cajas seleccionadas suma{" "}
            <strong>
              {totalEfectivoDeclaradoSeleccionado.toLocaleString("es-PY", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })} Gs.
            </strong>
            .
          </p>
          <p className="mb-0">
            La diferencia es{" "}
            <strong>
              {diferenciaVsDeclarado > 0 ? "+" : ""}
              {diferenciaVsDeclarado.toLocaleString("es-PY", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}{" "}
              Gs.
            </strong>
            . ¿Deseás continuar igualmente?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={cancelarConfirmacionDiferencia}>
            Cancelar
          </Button>
          <Button color="primary" onClick={confirmarRegistroConDiferencia}>
            Sí, registrar depósito
          </Button>
        </ModalFooter>
      </Modal>
      {permiteSeleccionCajas ? (
      <Modal isOpen={modalConflicto.visible} toggle={cerrarModalConflicto} centered>
        <ModalHeader toggle={cerrarModalConflicto}>Sucursales distintas</ModalHeader>
        <ModalBody>
          {modalConflicto.tipo === "cambioSucursal" ? (
            <p className="mb-0">
              Ya seleccionaste cajas de otra sucursal. Para cambiar de sucursal debés borrar la selección actual.
            </p>
          ) : (
            <p className="mb-0">
              No se permite generar un depósito con cajas de distintas sucursales. Borrá la selección para continuar con
              la nueva caja.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={cerrarModalConflicto}>
            Salir sin cambios
          </Button>
          <Button color="danger" onClick={confirmarModalConflicto}>
            Borrar selección
          </Button>
        </ModalFooter>
      </Modal>
      ) : null}
      <Modal isOpen={mostrarModalExito} centered backdrop="static">
        <ModalHeader>Depósito cargado</ModalHeader>
        <ModalBody>
          <p className="mb-2">Registramos tu depósito con éxito.</p>
          <p className="mb-0">¿Querés seguir cargando comprobantes o salir del módulo?</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={manejarSalirDeposito}>
            Salir
          </Button>
          <Button color="success" onClick={manejarSeguirCargando}>
            Seguir cargando
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
};

export default DepositoBoletaForm;
